import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetHolidays, useGetTimereports, useGetUserInfo, useGetWorkSchedule } from '../../../Core/Hooks/useTimereport';
import { formatLocalDate } from '../../../Core/Utils/dateUtils';
import './TimereportHourSummary.scss';
import { useTimereportSettings } from '../../../Core/Hooks/admin/useTimereportSettings';

const getMonday = (date: Date): Date => {
	const d = new Date(date);
	const day = d.getDay();
	const diff = d.getDate() - day + (day === 0 ? -6 : 1);
	d.setDate(diff);
	d.setHours(0, 0, 0, 0);
	return d;
};
type TimereportHourSummaryProps = {
	hideWeekend?: boolean;
	selectedDate?: string;
};

const TimereportHourSummary = ({ hideWeekend = true, selectedDate }: TimereportHourSummaryProps) => {
	const { t } = useTranslation();
	const { settings } = useTimereportSettings();
	const dayLabels = [
		t('TIMEREPORT_USER.REPORT_VIEW.SHARED.WEEKDAYS.MON'),
		t('TIMEREPORT_USER.REPORT_VIEW.SHARED.WEEKDAYS.TUE'),
		t('TIMEREPORT_USER.REPORT_VIEW.SHARED.WEEKDAYS.WED'),
		t('TIMEREPORT_USER.REPORT_VIEW.SHARED.WEEKDAYS.THU'),
		t('TIMEREPORT_USER.REPORT_VIEW.SHARED.WEEKDAYS.FRI'),
		t('TIMEREPORT_USER.REPORT_VIEW.SHARED.WEEKDAYS.SAT'),
		t('TIMEREPORT_USER.REPORT_VIEW.SHARED.WEEKDAYS.SUN'),
	];
	const monday = useMemo(() => {
		const base = selectedDate ? new Date(selectedDate + 'T00:00:00') : new Date();
		return getMonday(base);
	}, [selectedDate]);
	const sunday = useMemo(() => {
		const d = new Date(monday);
		d.setDate(d.getDate() + 6);
		return d;
	}, [monday]);

	const { timereports } = useGetTimereports(formatLocalDate(monday), formatLocalDate(sunday));
	const { workSchedule } = useGetWorkSchedule();
	const { userInfo } = useGetUserInfo();
	const { holidays } = useGetHolidays();

	const hoursByDay = useMemo(() => {
		const days: number[] = [0, 0, 0, 0, 0, 0, 0];
		if (!timereports) return days;
		for (const item of timereports) {
			const date = new Date(item.date);
			const dayOfWeek = date.getDay();
			const index = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
			days[index] += item.units;
		}
		return days;
	}, [timereports]);

	const scheduledByDay = useMemo(() => {
		if (workSchedule) {
			return [
				workSchedule.hoursMonday,
				workSchedule.hoursTuesday,
				workSchedule.hoursWednesday,
				workSchedule.hoursThursday,
				workSchedule.hoursFriday,
				workSchedule.hoursSaturday,
				workSchedule.hoursSunday,
			];
		}
		if (userInfo?.normalWeekHours) {
			const daily = userInfo.normalWeekHours / 5;
			return [daily, daily, daily, daily, daily, 0, 0];
		}
		return [0, 0, 0, 0, 0, 0, 0];
	}, [workSchedule, userInfo?.normalWeekHours]);

	// Subtract holiday hours from scheduled hours for each day of the week
	const holidayHoursByDay = useMemo(() => {
		const hours = [0, 0, 0, 0, 0, 0, 0];
		if (!holidays) return hours;
		const mondayStr = formatLocalDate(monday);
		const sundayStr = formatLocalDate(sunday);
		for (const h of holidays) {
			const dateKey = h.date.split('T')[0];
			if (dateKey >= mondayStr && dateKey <= sundayStr) {
				const d = new Date(dateKey + 'T00:00:00');
				const dayOfWeek = d.getDay();
				const index = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
				hours[index] += h.hours;
			}
		}
		return hours;
	}, [holidays, monday, sunday]);

	const totalHours = hoursByDay.reduce((sum, h) => sum + h, 0);
	const scheduledTotal = scheduledByDay.reduce((sum, h, i) => sum + Math.max(0, h - holidayHoursByDay[i]), 0);
	const flexBank = userInfo?.flexBank ?? 0;

	const isCurrentWeek = useMemo(() => {
		const now = getMonday(new Date());
		return monday.getTime() === now.getTime();
	}, [monday]);

	const weekNumber = useMemo(() => {
		const d = new Date(monday);
		d.setDate(d.getDate() + 3);
		const yearStart = new Date(d.getFullYear(), 0, 1);
		return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + yearStart.getDay() + 1) / 7);
	}, [monday]);

	const weekTitle = isCurrentWeek
		? t('TIMEREPORT_USER.REPORT_VIEW.HOUR_SUMMARY.TITLES.THIS_WEEK')
		: t('TIMEREPORT_USER.REPORT_VIEW.HOUR_SUMMARY.TITLES.WEEK', { weekNumber });

	return (
		<div className='biz-timereport-hour-summary'>
			<h3 className='biz-timereport-hour-summary__title'>{weekTitle}</h3>
			<div className='biz-timereport-hour-summary__rows'>
				{dayLabels.map((label, i) => {
					if (hideWeekend && i >= 5) return null;
					return (
						<div key={label} className='biz-timereport-hour-summary__row'>
							<span className='biz-timereport-hour-summary__label'>{label}:</span>
							<span
								className={`biz-timereport-hour-summary__value${hoursByDay[i] > 0 ? ' biz-timereport-hour-summary__value--filled' : ''}`}
							>
								{hoursByDay[i]}
							</span>
						</div>
					);
				})}
				<div className='biz-timereport-hour-summary__divider' />
				<div className='biz-timereport-hour-summary__row biz-timereport-hour-summary__row--total'>
					<span className='biz-timereport-hour-summary__label'>
						{t('TIMEREPORT_USER.REPORT_VIEW.HOUR_SUMMARY.LABELS.TOTAL_HOURS')}
					</span>
					<span className='biz-timereport-hour-summary__value'>
						{totalHours} {scheduledTotal > 0 ? `/ ${scheduledTotal}` : ''}
					</span>
				</div>
				{settings.flexEnabled && (
					<div className='biz-timereport-hour-summary__row'>
						<span className='biz-timereport-hour-summary__label'>
							{t('TIMEREPORT_USER.REPORT_VIEW.HOUR_SUMMARY.LABELS.CURRENT_FLEX')}
						</span>
						<span
							className={`biz-timereport-hour-summary__value${flexBank < 0 ? ' biz-timereport-hour-summary__value--negative' : ''}`}
						>
							{flexBank}
						</span>
					</div>
				)}
			</div>
		</div>
	);
};

export default TimereportHourSummary;
